/**
 * Plate Data Mapper Utility
 * Maps extracted vehicle license plate data to IDs from plate_data.json
 */

interface PlateColor {
  id: number;
  label: string;
}

interface PlateSource {
  id: number;
  label: string;
}

interface PlateCategory {
  id: number;
  label: string;
}

interface PlateData {
  colors: PlateColor[];
  sources: PlateSource[];
  categories: PlateCategory[];
}

let plateDataCache: PlateData | null = null;

/**
 * Load plate data from public folder
 */
export async function loadPlateData(): Promise<PlateData> {
  if (plateDataCache) {
    return plateDataCache;
  }

  try {
    const response = await fetch('/plate_data.json');
    if (!response.ok) {
      throw new Error('Failed to load plate data');
    }
    plateDataCache = await response.json();
    return plateDataCache!;
  } catch (error) {
    console.error('Error loading plate data:', error);
    throw error;
  }
}

/**
 * Normalize Arabic text by removing diacritics and normalizing characters
 */
export function normalizeArabicText(text: string): string {
  if (!text) return '';
  
  return text
    .trim()
    .toLowerCase()
    // Remove Arabic diacritics (tashkeel)
    .replace(/[\u064B-\u065F\u0670]/g, '')
    // Normalize Arabic letters
    .replace(/[إأآا]/g, 'ا')
    .replace(/[ى]/g, 'ي')
    .replace(/ة/g, 'ه')
    // Remove extra spaces
    .replace(/\s+/g, ' ');
}

/**
 * Calculate similarity score between two strings (0-1)
 * Uses Levenshtein distance
 */
function calculateSimilarity(str1: string, str2: string): number {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 1;
  
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  
  if (longer.length === 0) return 1;
  
  const editDistance = levenshteinDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
}

/**
 * Map color label to color ID
 * Handles both English labels (B, A, WHITE, RED, etc.) and codes
 */
export async function mapColorToId(colorLabel: string): Promise<{
  id: number | null;
  matchedLabel: string;
  confidence: number;
}> {
  const plateData = await loadPlateData();
  
  if (!colorLabel || colorLabel.trim() === '') {
    return { id: null, matchedLabel: 'UNKNOWN', confidence: 0 };
  }
  
  const normalizedInput = colorLabel.trim().toUpperCase();
  
  // Exact match first
  const exactMatch = plateData.colors.find(
    (c) => c.label.toUpperCase() === normalizedInput
  );
  
  if (exactMatch) {
    return { id: exactMatch.id, matchedLabel: exactMatch.label, confidence: 1.0 };
  }
  
  // Fuzzy match
  let bestMatch = { id: null as number | null, matchedLabel: 'UNKNOWN', confidence: 0 };
  
  for (const color of plateData.colors) {
    const similarity = calculateSimilarity(normalizedInput, color.label);
    
    if (similarity > bestMatch.confidence && similarity >= 0.6) {
      bestMatch = {
        id: color.id,
        matchedLabel: color.label,
        confidence: similarity,
      };
    }
  }
  
  return bestMatch;
}

/**
 * Map source label (Arabic) to source ID
 * Handles Arabic text variations and diacritics
 */
export async function mapSourceToId(sourceLabel: string): Promise<{
  id: number | null;
  matchedLabel: string;
  confidence: number;
}> {
  const plateData = await loadPlateData();
  
  if (!sourceLabel || sourceLabel.trim() === '') {
    return { id: null, matchedLabel: 'غير معروف', confidence: 0 };
  }
  
  const normalizedInput = normalizeArabicText(sourceLabel);
  
  // Exact match after normalization
  const exactMatch = plateData.sources.find(
    (s) => normalizeArabicText(s.label) === normalizedInput
  );
  
  if (exactMatch) {
    return { id: exactMatch.id, matchedLabel: exactMatch.label, confidence: 1.0 };
  }
  
  // Fuzzy match
  let bestMatch = { id: null as number | null, matchedLabel: 'غير معروف', confidence: 0 };
  
  for (const source of plateData.sources) {
    const normalizedLabel = normalizeArabicText(source.label);
    const similarity = calculateSimilarity(normalizedInput, normalizedLabel);
    
    if (similarity > bestMatch.confidence && similarity >= 0.6) {
      bestMatch = {
        id: source.id,
        matchedLabel: source.label,
        confidence: similarity,
      };
    }
  }
  
  return bestMatch;
}

/**
 * Map category label (Arabic) to category ID
 * Handles Arabic text variations and diacritics
 */
export async function mapCategoryToId(categoryLabel: string): Promise<{
  id: number | null;
  matchedLabel: string;
  confidence: number;
}> {
  const plateData = await loadPlateData();
  
  if (!categoryLabel || categoryLabel.trim() === '') {
    return { id: null, matchedLabel: 'غير معروف', confidence: 0 };
  }
  
  const normalizedInput = normalizeArabicText(categoryLabel);
  
  // Exact match after normalization
  const exactMatch = plateData.categories.find(
    (c) => normalizeArabicText(c.label) === normalizedInput
  );
  
  if (exactMatch) {
    return { id: exactMatch.id, matchedLabel: exactMatch.label, confidence: 1.0 };
  }
  
  // Fuzzy match
  let bestMatch = { id: null as number | null, matchedLabel: 'غير معروف', confidence: 0 };
  
  for (const category of plateData.categories) {
    const normalizedLabel = normalizeArabicText(category.label);
    const similarity = calculateSimilarity(normalizedInput, normalizedLabel);
    
    if (similarity > bestMatch.confidence && similarity >= 0.6) {
      bestMatch = {
        id: category.id,
        matchedLabel: category.label,
        confidence: similarity,
      };
    }
  }
  
  return bestMatch;
}

/**
 * Map all plate data at once
 */
export async function mapPlateData(
  plateColor: string,
  plateNumber: string,
  plateSource: string,
  plateCategory: string
): Promise<{
  colorId: number | null;
  colorLabel: string;
  colorConfidence: number;
  plateNumber: string;
  sourceId: number | null;
  sourceLabel: string;
  sourceConfidence: number;
  categoryId: number | null;
  categoryLabel: string;
  categoryConfidence: number;
}> {
  const [colorResult, sourceResult, categoryResult] = await Promise.all([
    mapColorToId(plateColor),
    mapSourceToId(plateSource),
    mapCategoryToId(plateCategory),
  ]);
  
  return {
    colorId: colorResult.id,
    colorLabel: colorResult.matchedLabel,
    colorConfidence: colorResult.confidence,
    plateNumber: plateNumber.trim(),
    sourceId: sourceResult.id,
    sourceLabel: sourceResult.matchedLabel,
    sourceConfidence: sourceResult.confidence,
    categoryId: categoryResult.id,
    categoryLabel: categoryResult.matchedLabel,
    categoryConfidence: categoryResult.confidence,
  };
}

